import * as React from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useIntl } from 'react-intl';
import AdditionalInformation from './AdditionalInformation';
import Events from './Events';
import InfoTableModal from './InfoTableModal';
import CommentsAndTags from './CommentsAndTags';
import GdprService from '../../../Services/GdprService';
import PermissionsService, {
  PERMISSION_RIDE_POSITIONS,
  PERMISSION_RIDE_SHOW_AND_PLAY,
} from '../../../Services/PermissionsService';
import { localForageStore } from '../../../store/localForageStore';
import { IVehicles } from '../../Vehicles/types';
import { URL_TRIP_MAP } from '../../../router/constants';
import { ITripsOverViewRecord } from '../interfaces';
import { IS_MANUAL_RIDE } from '../constants';
import { vehiclesAtom } from '../../../store/recoil/vehicles';
import { isModalFormOpenedAtom, tripDataAtom } from '../TripsOverviewState';
import useAnalytics from '../../../hooks/useAnalytics';

interface IDetailRowProps {
  data: ITripsOverViewRecord;
  datacyIndex: number;
}

function DetailRow(props: IDetailRowProps): JSX.Element {
  const { formatMessage: f } = useIntl();
  const vehicles = useRecoilValue<IVehicles>(vehiclesAtom);
  const [showModal, setShowModal] = React.useState<boolean>(false);
  const [allowedActionsByGdpr, setAllowedActionsByGdpr] = React.useState<string[]>([]);
  const [, setIsModalFormOpened] = useRecoilState<string>(isModalFormOpenedAtom);
  const [, setTripData] = useRecoilState<ITripsOverViewRecord | null>(tripDataAtom);
  const { trackEvent } = useAnalytics();

  const getIsRideShowPermission = () => {
    return PermissionsService.hasAccess(
      PERMISSION_RIDE_SHOW_AND_PLAY,
      ['read'],
      props.data.customer.id
    );
  };

  const getIsRidePositionsPermission = () => {
    return PermissionsService.hasAccess(
      PERMISSION_RIDE_POSITIONS,
      ['read'],
      props.data.customer.id
    );
  };

  const handleAllowedType = React.useCallback(async () => {
    const allowedTypes = await GdprService.getRideGdprByTypes(
      ['showRide', 'showRideDetail'],
      props.data.isPrivate,
      props.data.customer.id,
      props.data.driver.id
    );
    setAllowedActionsByGdpr(allowedTypes);
  }, [props.data.customer.id, props.data.driver.id, props.data.isPrivate]);

  React.useEffect(() => {
    handleAllowedType();
  }, [handleAllowedType]);

  const handleShowModal = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    setShowModal(!showModal);
    trackEvent(['ride book', 'click', `Ride book open detail row modal(Suster btn)`]);
  };

  const handleCancelModal = () => {
    setShowModal(false);
    trackEvent(['ride book', 'click', `Ride book close detail row modal`]);
  };

  const redirectToTripMap = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    trackEvent(['ride book', 'click', 'Ride book show ride in new tab']);

    localForageStore.setItem('rides', [`${props.data.customer.id}-${props.data.id}`]);
    localForageStore.setItem('multi-rides', null);
    window.open(`${URL_TRIP_MAP}`, '_blank');
  };

  const getVehicleName = (id: number) => {
    const vehicle = Object.values(vehicles).find((i) => i.id === id);
    if (vehicle && vehicle.name) {
      return vehicle.name;
    }
    return '';
  };

  const handleModalForm = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
    const type = event.currentTarget.getAttribute('data-type');
    if (type) {
      trackEvent(['ride book', 'click', `Ride book modal detail ${type}`]);
      const vehicleName = getVehicleName(props.data.vehicle.id);
      const data = { ...props.data, vehicleName };

      setIsModalFormOpened(type);
      setTripData(data);
    }
  };

  const getSummary = () => {
    const data = {};
    Object.keys(props.data.info.summary).forEach((key: string) => {
      data[key] =
        props.data.info.summary[key] + (props.data.parking ? props.data.parking.summary[key] : 0);
    });
    return data;
  };

  const element = document.getElementById('table-overview-header');

  let detailWidth = '1450px';

  if (element) {
    detailWidth = `${element.scrollWidth - 100}px`;
  }

  const renderAllowedRideShowElement = () => {
    return allowedActionsByGdpr.includes('showRide') ? (
      <a
        href="#"
        title={f({ id: 'rides.rideMap' })}
        className="text-gray-lighten"
        onClick={redirectToTripMap}
        data-cy="ride-book-show-ride"
      >
        <i className="cmd-icons cmd-icon-play-simple fs-4" />
        <span className="sr-only">{f({ id: 'rides.rideMap' })}</span>
      </a>
    ) : (
      <i
        title={f({ id: 'error.rideBooks.actionUnavailableDueToGDPR' })}
        className="cmd-icons cmd-icon-play-simple-lock fs-4"
        data-cy="ride-book-show-ride-gdpr"
      />
    );
  };

  const renderAllowedRidePositionsElement = () => {
    return allowedActionsByGdpr.includes('showRideDetail') ? (
      <a
        href="#"
        title={f({ id: 'tables.tripsOverview.actions.ridePosition' })}
        className="text-gray-lighten"
        onClick={handleModalForm}
        data-type="detail"
        data-cy="ride-book-show-ride-detail"
      >
        <i className="cmd-icons cmd-icon-ride-detail fs-4" />
        <span className="sr-only">{f({ id: 'tables.tripsOverview.actions.ridePosition' })}</span>
      </a>
    ) : (
      <i
        title={f({ id: 'error.rideBooks.actionUnavailableDueToGDPR' })}
        className="cmd-icons cmd-icon-ride-detail-lock fs-4"
        data-cy="ride-book-show-ride-detail-gdpr"
      />
    );
  };

  const renderRideShowElement = () => {
    return (
      <div className="sub-actions">
        {getIsRideShowPermission() ? (
          renderAllowedRideShowElement()
        ) : (
          <i
            className="cmd-icons text-gray-light cmd-icon-play-simple fs-4"
            data-cy="ride-book-show-ride-disabled"
          />
        )}
      </div>
    );
  };

  const renderRidePositions = () => {
    return (
      <div>
        {getIsRidePositionsPermission() ? (
          renderAllowedRidePositionsElement()
        ) : (
          <i
            className="cmd-icons text-gray-light cmd-icon-ride-detail fs-4"
            data-cy="ride-book-show-ride-detail-disabled"
          />
        )}
      </div>
    );
  };

  return (
    <div className="b-table-prehlad-jazd-row-detail">
      <div
        className="align-middle text-center justify-content-center b-table-prehlad-jazd-checkbox"
        style={{
          minWidth: '40px',
          maxWidth: '40px',
          width: '40px',
        }}
      />
      <div style={{ minWidth: `${detailWidth}`, flex: 1 }}>
        <table className="table table-bordered m-0 h-100 border-0">
          <tbody>
            <tr className="tr-sm">
              <td className="p-0" style={{ width: '530px', minWidth: '530px', maxWidth: '530px' }}>
                {props.data && props.data && <AdditionalInformation data={props.data} />}
              </td>
              <td
                className="p-0 position-relative border-right-0"
                style={{ width: '530px', minWidth: '530px', maxWidth: '530px' }}
              >
                <Events
                  rideId={props.data.id}
                  customerId={props.data.customer.id}
                  recordsCount={props.data.info.recordsCount}
                  start={props.data.timestamp.start}
                  isAnonym={!props.data.address.startLink}
                  datacyIndex={props.datacyIndex}
                />
              </td>
              <td
                className="p-0 position-relative border-left-0 border-right-0"
                style={{ width: '530px', minWidth: '530px', maxWidth: '530px' }}
              >
                <CommentsAndTags
                  rideId={props.data.id}
                  customerId={props.data.customer.id}
                  locked={props.data.locked}
                  rideComment={props.data.info.rideComment}
                  parkingComment={props.data.info.parkingComment}
                  rideTags={props.data.info.rideTags}
                  parkingTags={props.data.info.parkingTags}
                />
                <a
                  href="#"
                  title={f({ id: 'table.rideBook.modal.button' })}
                  className="e-button-sq border-on floated-button"
                  onClick={handleShowModal}
                  data-cy="ride-book-row-detail-show"
                >
                  <i className="cmd-icons cmd-icon-listing-alternative fs-4" />
                  <span className="sr-only">{f({ id: 'table.rideBook.modal.button' })}</span>
                </a>
                {showModal && (
                  <InfoTableModal
                    rideId={props.data.id}
                    customerId={props.data.customer.id}
                    recordsCount={props.data.info.recordsCount}
                    cancelCallback={handleCancelModal}
                    locked={props.data.locked}
                    rideComment={props.data.info.rideComment}
                    parkingComment={props.data.info.parkingComment}
                    rideTags={props.data.info.rideTags}
                    parkingTags={props.data.info.parkingTags}
                    summary={getSummary()}
                    start={props.data.timestamp.start}
                    isAnonym={!props.data.address.startLink}
                  />
                )}
              </td>
              <td className="border-left-0" />
            </tr>
          </tbody>
        </table>
      </div>
      <div className="text-center align-middle sticky-col">
        <div className="actions flex-column">
          {props.data.source && props.data.source !== IS_MANUAL_RIDE && renderRideShowElement()}
          {props.data.source && props.data.source !== IS_MANUAL_RIDE && (
            <div className="mt-6">{renderRidePositions()}</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DetailRow;
